import { useState, useCallback, type MutableRefObject } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Markdown } from "tiptap-markdown"
import Heading from "@tiptap/extension-heading"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { TableHeader } from "@tiptap/extension-table-header"
import { TableCell } from "@tiptap/extension-table-cell"
import Underline from "@tiptap/extension-underline"
import { RefreshCw, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useTiptapEditor } from "@/features/workspace/hooks/useTiptapEditor"
import { cn } from "@/lib/utils"
import { ProtectHeadings } from "@/features/workspace/extensions/ProtectHeadings"

interface DocumentEditorProps {
  content: string
  onUpdate?: (markdown: string) => void
  className?: string
  /** Called at once on every user edit (before the debounced `onUpdate`). */
  onUserEdit?: () => void
  /** Filled with a function that delivers a pending debounced edit to `onUpdate` now. */
  flushRef?: MutableRefObject<(() => void) | null>
}

export function DocumentEditor({ content, onUpdate, className, onUserEdit, flushRef }: DocumentEditorProps) {
  const [hasError, setHasError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)

  const safeContent =
    content.length > 500_000
      ? content.slice(0, 500_000) + "\n\n---\n\n*[Content truncated for performance]*"
      : content

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ heading: false }),
        Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
        ProtectHeadings as any,
        Markdown.configure({ html: true, tightLists: true }),
        Link.configure({ openOnClick: false }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Placeholder.configure({ placeholder: "Write something…" }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        Underline,
      ],
      content: safeContent,
      editable: true,
      immediatelyRender: false,
      editorProps: {
        attributes: {
          class: cn(
            "prose focus:outline-none max-w-4xl mx-auto min-h-[500px] px-[54px] py-10 bg-white shadow-sm",
            className
          ),
        },
      },
    },
    [retryKey]
  )

  const { flush } = useTiptapEditor({ editor, isEditing: true, content: safeContent, onUpdate, onUserEdit })
  if (flushRef) flushRef.current = flush

  const handleRetry = useCallback(() => {
    setHasError(false)
    setRetryKey((k) => k + 1)
  }, [])

  if (hasError) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-muted/20 rounded-lg border p-8">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
        <h3 className="text-base font-semibold mb-1">Editor failed to load</h3>
        <p className="text-sm text-muted-foreground text-center mb-4 max-w-sm">
          This can happen with very large documents.
        </p>
        <Button variant="outline" onClick={handleRetry} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Try again
        </Button>
      </div>
    )
  }

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="w-6 h-6 border-2 border-muted border-t-foreground rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-muted/20">
      <EditorContent editor={editor} />
    </div>
  )
}
